import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './Select.css';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectProps {
  value?: string | number;
  options: SelectOption[];
  placeholder?: string;
  onChange?: (value: string | number) => void;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
}

export default function Select({
  value,
  options,
  placeholder = 'Please select',
  onChange,
  disabled = false,
  className = '',
  searchable = false,
  searchPlaceholder = 'Search...',
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | number | undefined>(value);
  const [searchValue, setSearchValue] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync external value
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Calculate dropdown position
  const calculateDropdownStyle = useCallback((): React.CSSProperties => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom - 4;
    // Max dropdown height from CSS (256px) + search box if searchable
    const maxDropdownHeight = searchable ? 300 : 260;

    // If not enough space below for max height, show dropdown above
    const showAbove = spaceBelow < maxDropdownHeight;

    return {
      position: 'fixed',
      top: showAbove ? 'auto' : rect.bottom + 4,
      bottom: showAbove ? viewportHeight - rect.top + 4 : 'auto',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    };
  }, [searchable]);

  // Calculate position synchronously when dropdown opens
  useLayoutEffect(() => {
    if (isOpen) {
      setDropdownStyle(calculateDropdownStyle());
    }
  }, [isOpen, calculateDropdownStyle]);

  // Update position on scroll/resize when open
  useEffect(() => {
    if (!isOpen) return;
    const handleUpdate = () => setDropdownStyle(calculateDropdownStyle());
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, calculateDropdownStyle]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find(opt => opt.value === selectedValue);

  // Filter options (when searchable)
  const filteredOptions = searchable
    ? options.filter(option => option.label.toLowerCase().includes(searchValue.toLowerCase()))
    : options;

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
    if (!isOpen) {
      setSearchValue('');
    }
  }, [isOpen, searchable]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(prev => !prev);
  };

  const handleSelect = (optionValue: string | number) => {
    setSelectedValue(optionValue);
    onChange?.(optionValue);
    setIsOpen(false);
    setSearchValue('');
  };

  // Render dropdown via Portal to body
  const dropdownContent = isOpen
    ? ReactDOM.createPortal(
        <div ref={dropdownRef} className="select-dropdown" style={dropdownStyle}>
          {/* Search box */}
          {searchable && (
            <div className="select-search">
              <span className="search-icon">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </span>
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              {searchValue && (
                <button
                  className="search-clear"
                  onClick={e => {
                    e.stopPropagation();
                    setSearchValue('');
                    searchInputRef.current?.focus();
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}
          <div className="select-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className={`select-option ${option.value === selectedValue ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className="option-label">{option.label}</span>
                  {option.value === selectedValue && (
                    <span className="option-check">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="select-empty">No matching options</div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={containerRef}
      className={`custom-select ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
    >
      {/* Trigger */}
      <div ref={triggerRef} className="select-trigger" onClick={handleToggle}>
        <span className={`select-value ${!selectedOption ? 'placeholder' : ''}`}>
          {selectedOption?.label || placeholder}
        </span>
        <span className="select-arrow">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      {dropdownContent}
    </div>
  );
}
